<template>
  <section>
    <ul>
      <li v-for="item in rootItems" :key="item.id" class="px-24 py-20">
        <NavBurgerMenuItem
          :color="item.color"
          :icon="item.icon"
          :title="item.title"
          :url="item.url"
          @click-item="toggle"
        />
      </li>

      <template v-if="isNavigationServicesRevampEnabled">
        <li class="px-24 py-20">
          <NavBurgerMenuShop :category-items :shop-items @click-item="toggle" />
        </li>

        <li class="px-24 py-20">
          <NavBurgerMenuItem
            icon="Swap"
            :title="i18n(translations.tradeIn)"
            :tracking="tradeInTracking"
            :url="tradeInUrl"
            @click-item="toggle"
          />
        </li>

        <li class="px-24 py-20">
          <NavBurgerMenuUniverse
            id="extended-services"
            icon="Quality"
            :title="i18n(translations.extendedServices)"
          >
            <NavBurgerMenuHeader
              :title="i18n(translations.extendedServices)"
              @back="clearActiveMenu"
              @close="toggle"
            />
            <ul
              class="grid grid-cols-1 gap-x-16 gap-y-32 overflow-auto px-24 pb-32 pt-24 sm:grid-cols-2 md:grid-cols-3"
            >
              <li v-for="card in cards" :key="card.title">
                <NavBurgerMenuCategory
                  :image="card.image"
                  :title="card.title"
                  :tracking="{
                    zone: 'mega_menu',
                    name: 'services',
                    value: card.trackingValue,
                  }"
                  :url="card.ctaUrl"
                  @click="toggle"
                />
              </li>
            </ul>
          </NavBurgerMenuUniverse>
        </li>

        <li v-if="mobilePlansUrl" class="px-24 py-20">
          <NavBurgerMenuItem
            icon="Smartphone"
            :title="i18n(translations.mobilePlans)"
            :tracking="mobilePlansTracking"
            :url="mobilePlansUrl"
            @click-item="toggle"
          />
        </li>
      </template>

      <template v-else>
        <li v-for="item in shopItems" :key="item.id" class="px-24 py-20">
          <NavBurgerMenuItem
            :color="item.color"
            :icon="item.icon"
            :title="item.title"
            :url="item.url"
            @click-item="toggle"
          />
        </li>
        <li
          v-for="category in categoryItems"
          :key="category.id"
          class="px-24 py-20"
        >
          <NavBurgerMenuCategoryUniverse
            :item="category"
            @click-item="toggle"
          />
        </li>
      </template>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { useActiveMenu } from '~/scopes/navigation/composables/useActiveMenu'
import { useBurgerMenuState } from '~/scopes/navigation/composables/useBurgerMenuState'
import { useExtendedServicesCards } from '~/scopes/navigation/composables/useExtendedServicesCards'
import { useFetchNavigationItems } from '~/scopes/navigation/composables/useFetchNavigationItems'
import { useMobilePlansUrl } from '~/scopes/navigation/composables/useMobilePlansUrl'
import { useTradeInUrl } from '~/scopes/navigation/composables/useTradeInUrl'
import type { NavigationItem } from '~/scopes/navigation/types'

import NavBurgerMenuCategory from './NavBurgerMenuCategory.vue'
import NavBurgerMenuCategoryUniverse from './NavBurgerMenuCategoryUniverse.vue'
import NavBurgerMenuHeader from './NavBurgerMenuHeader.vue'
import NavBurgerMenuItem from './NavBurgerMenuItem.vue'
import translations from './NavBurgerMenuItemList.translations'
import NavBurgerMenuShop from './NavBurgerMenuShop.vue'
import NavBurgerMenuUniverse from './NavBurgerMenuUniverse.vue'

const { items } = useFetchNavigationItems()
const { toggle } = useBurgerMenuState()
const { clearActiveMenu } = useActiveMenu()

const i18n = useI18n()
const { cards } = useExtendedServicesCards()

const experiments = useExperiments()
const isNavigationServicesRevampEnabled = computed(
  () => experiments['experiment.navigationServicesRevamp'] === 'withServices',
)

const { tradeInUrl } = useTradeInUrl()
const { mobilePlansUrl } = useMobilePlansUrl()

const tradeInTracking = {
  zone: 'mega_menu',
  name: 'trade_in',
  value: 'trade_in',
}
const mobilePlansTracking = {
  zone: 'mega_menu',
  name: 'mobile_plans',
  value: 'mobile_plans',
}

// Universes/categories (incl. "Obsolete", which has no children) go in
// Shop; events, gift cards, etc. stay at the root.
const SHOP_TYPES = ['universe', 'category']
const isShopType = (item: NavigationItem) =>
  SHOP_TYPES.includes(item.type?.toLowerCase())

const rootItems = computed(() =>
  items.value.filter((item) => !item.children.length && !isShopType(item)),
)
const shopItems = computed(() =>
  items.value.filter((item) => !item.children.length && isShopType(item)),
)
const categoryItems = computed(() =>
  items.value.filter((item) => item.children.length),
)
</script>
